import { useGetIntegrations } from "../../lib/hooks"
import { useEffect, useState, useCallback, type ReactNode } from "react"
import { client } from "@/lib/ts-rest"
import { trackEvent } from "@/lib/log"
import { CheckBadgeIcon } from "@heroicons/react/24/outline"
import {
  ArrowRightIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline"
import { RotateCwIcon } from "lucide-react"
import { useToast } from "../ui/use-toast.ts"
import { Button } from "../ui/button.tsx"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog.tsx"
import { useMediaQuery } from "react-responsive"

type StepImage = {
  source: string
  click: { x: number; y: number }
}

type SubStep = {
  content: ReactNode
  image: StepImage
}

type Step = {
  title: ReactNode
  subSteps: SubStep[]
}

const steps: Step[] = [
  {
    title: "Open Routines",
    subSteps: [
      {
        content: "Select the menu tab at the bottom of the screen",
        image: { source: "/steps/1a.jpeg", click: { x: 42, y: 91 } },
      },
      {
        content: "Select Routines",
        image: { source: "/steps/1b.jpeg", click: { x: 37, y: 36 } },
      },
      {
        content: "Select the plus icon to add a new routine",
        image: { source: "/steps/1c.jpeg", click: { x: 91, y: 8 } },
      },
    ],
  },
  {
    title: "Add the routine trigger",
    subSteps: [
      {
        content: "Select Add an event",
        image: { source: "/steps/2a.jpeg", click: { x: 88, y: 31 } },
      },
      {
        content: "Select Smart Home",
        image: { source: "/steps/2b.jpeg", click: { x: 50, y: 36 } },
      },
      {
        content: "Select Pillars Speaker",
        image: { source: "/steps/2c.jpeg", click: { x: 53, y: 15.3 } },
      },
      {
        content: (
          <>
            Select When Contact Sensor is <strong>Open</strong>
          </>
        ),
        image: { source: "/steps/2d.jpeg", click: { x: 92.5, y: 29.5 } },
      },
    ],
  },
  {
    title: "Add the routine action",
    subSteps: [
      {
        content: "Select Add an action",
        image: { source: "/steps/3a.jpeg", click: { x: 88, y: 57 } },
      },
      {
        content: "Select Skills",
        image: { source: "/steps/3b.jpeg", click: { x: 35, y: 46 } },
      },
      {
        content: "Select Your Skills, then Pillars",
        image: { source: "/steps/3c.jpeg", click: { x: 30, y: 15.3 } },
      },
      {
        content: "Select Play Athan",
        image: { source: "/steps/3e.jpeg", click: { x: 34, y: 24 } },
      },
      {
        content: <>Choose a device and press the blue Save button</>,
        image: { source: "/steps/3f.jpeg", click: { x: 63, y: 69.7 } },
      },
    ],
  },
]

const allImages = steps.flatMap((step, stepIndex) =>
  step.subSteps.map((subStep, subIndex) => ({
    ...subStep.image,
    stepIndex,
    subIndex,
  })),
)

function ClickDot({
  x,
  y,
  isModal = false,
}: {
  x: number
  y: number
  isModal?: boolean
}) {
  return (
    <div
      className="absolute pointer-events-none"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        transform: "translate(-50%, -50%)",
      }}
    >
      <div
        className={`${isModal ? "h-5 w-5" : "h-3 w-3"} rounded-full bg-red-500 shadow-md`}
        style={{ animation: "click-dot-pulse 2s ease-in-out infinite" }}
      />
      <style>{`
        @keyframes click-dot-pulse {
          0% { opacity: 0 }
          25% { opacity: 1 }
          50% { opacity: 1 }
          75% { opacity: 0 }
          100% { opacity: 0 }
        }
      `}</style>
    </div>
  )
}

function ImageModal({
  imageIndex,
  onClose,
  onPrev,
  onNext,
  onFinish,
}: {
  imageIndex: number
  onClose: () => void
  onPrev: () => void
  onNext: () => void
  onFinish: () => void
}) {
  const image = allImages[imageIndex]!
  const step = steps[image.stepIndex]!
  const subStep = step.subSteps[image.subIndex]!
  const hasPrev = imageIndex > 0
  const hasNext = imageIndex < allImages.length - 1

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && hasPrev) onPrev()
      if (e.key === "ArrowRight" && hasNext) onNext()
    },
    [onPrev, onNext, hasPrev, hasNext],
  )

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [handleKeyDown])

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md h-[90vh] flex flex-col gap-0 p-0">
        <DialogHeader className="shrink-0">
          <div className="p-4 pb-3">
            <DialogTitle className="text-sm font-medium">
              Step {image.stepIndex + 1} of {steps.length}: {step.title}
            </DialogTitle>
            <DialogDescription>{subStep.content}</DialogDescription>
          </div>
          <div className="h-1 w-full bg-gray-100">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{
                width: `${((imageIndex + 1) / allImages.length) * 100}%`,
              }}
            />
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex items-center justify-center p-4">
          <div className="relative flex items-center gap-3 w-full h-full">
            <Button
              size="icon"
              onClick={onPrev}
              className={`shrink-0 border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-500 ${!hasPrev ? "invisible" : ""}`}
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </Button>
            <div className="flex-1 flex items-center justify-center h-full">
              <div className="relative inline-block overflow-hidden rounded-xl">
                <img
                  src={image.source}
                  className="max-w-full max-h-[calc(90vh-10rem)] object-contain block"
                />
                <ClickDot x={image.click.x} y={image.click.y} isModal />
              </div>
            </div>
            <Button
              size="icon"
              onClick={hasNext ? onNext : onFinish}
              className="shrink-0 border border-green-200 bg-green-50 hover:bg-green-100 text-green-700"
            >
              {hasNext ? (
                <CheckIcon className="h-5 w-5" />
              ) : (
                <ArrowRightIcon className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function getGlobalImageIndex(stepIndex: number, subIndex: number) {
  return allImages.findIndex(
    (img) => img.stepIndex === stepIndex && img.subIndex === subIndex,
  )
}

export function SetUpDevices({ integrationId }: { integrationId: number }) {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" })
  const { data, refetch } = useGetIntegrations()
  const integration = data?.body.integrations.find(
    (item) => item.integration.id === Number(integrationId),
  )
  const { mutate: sendNotificationsForIntegration } =
    client.sendNotificationsForIntegration.useMutation({
      onSuccess: () => {
        trackEvent("played test athan", { integrationId, phase })
        toast({ title: "The athan should now be playing." })
        setPollIntegrations(true)
      },
    })
  const { toast } = useToast()

  const [pollIntegrations, setPollIntegrations] = useState(false)
  const [modalImageIndex, setModalImageIndex] = useState<number | null>(null)

  const isComplete = integration?.integration.isSetupComplete
  const isVerifying = pollIntegrations && !isComplete
  const phase = isComplete ? "after" : isVerifying ? "during" : "before"

  useEffect(() => {
    if (isVerifying) {
      const interval = setInterval(() => refetch(), 3000)
      return () => clearInterval(interval)
    }
  }, [isVerifying, refetch])

  useEffect(() => {
    if (isComplete && pollIntegrations) {
      setPollIntegrations(false)
    }
  }, [isComplete, pollIntegrations])

  return (
    <div className="flex flex-col gap-4">
      {phase === "before" && (
        <div className="flex flex-col gap-4 text-sm">
          <div className="text-primary font-medium">1. Open your Alexa app</div>
          <div className="flex flex-col gap-2">
            <div className="text-primary font-medium">
              2. Follow this 2 minute guide
            </div>
            <div className="ml-4">
              <Button onClick={() => {
                trackEvent("started onboarding guide", { integrationId })
                setModalImageIndex(0)
              }}>
                Start
                <ArrowRightIcon className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-primary font-medium">3. Test the athan</div>
            <div className="ml-4">
              <Button
                variant="outline"
                onClick={() => {
                  sendNotificationsForIntegration({
                    params: { integrationId: String(integrationId) },
                  })
                }}
              >
                Play athan
              </Button>
            </div>
          </div>
        </div>
      )}

      {phase === "during" && (
        <>
          <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-4 w-fit">
            <RotateCwIcon className="h-6 w-6 shrink-0 animate-spin" />
            <span className="font-medium">Verifying that athan is playing</span>
          </div>
          <div className={`flex gap-2 ${isMobile ? "flex-col" : "flex-row"}`}>
            <Button
              variant="outline"
              className="w-fit"
              onClick={() => {
                sendNotificationsForIntegration({
                  params: { integrationId: String(integrationId) },
                })
              }}
            >
              <RotateCwIcon className="h-4 w-4 mr-2" />
              Test athan again
            </Button>
            <Button
              variant="outline"
              className="w-fit"
              onClick={() => setModalImageIndex(0)}
            >
              Show instructions
            </Button>
          </div>
        </>
      )}

      {phase === "after" && (
        <>
          <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-xl p-4 w-fit">
            <CheckBadgeIcon className="h-6 w-6 shrink-0" />
            <span className="font-medium">Athan automation complete</span>
          </div>
          <div className={`flex gap-2 ${isMobile ? "flex-col" : "flex-row"}`}>
            <Button
              variant="outline"
              className="w-fit"
              onClick={() => {
                sendNotificationsForIntegration({
                  params: { integrationId: String(integrationId) },
                })
              }}
            >
              Play athan
            </Button>
            <Button
              variant="outline"
              className="w-fit"
              onClick={() => setModalImageIndex(0)}
            >
              Show instructions
            </Button>
          </div>
        </>
      )}

      {modalImageIndex !== null && (
        <ImageModal
          imageIndex={modalImageIndex}
          onClose={() => setModalImageIndex(null)}
          onPrev={() => setModalImageIndex((i) => Math.max(0, (i ?? 0) - 1))}
          onNext={() =>
            setModalImageIndex((i) =>
              Math.min(allImages.length - 1, (i ?? 0) + 1),
            )
          }
          onFinish={() => {
            trackEvent("completed onboarding guide", { integrationId })
            setModalImageIndex(null)
            sendNotificationsForIntegration({
              params: { integrationId: String(integrationId) },
            })
          }}
        />
      )}
    </div>
  )
}
